import type { CommandProps } from '@tiptap/core';
import { mergeAttributes } from '@tiptap/core';
import TiptapBulletList from '@tiptap/extension-bullet-list';
import { applyListToggleColors } from '../../helpers/listStyleHelpers';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customBulletList: {
      toggleBulletList: () => ReturnType;
      toggleBulletListType: (type?: string) => ReturnType;
    };
  }
}
export const BulletList = TiptapBulletList.extend({
  addAttributes() {
    return {
      type: {
        default: 'disc',
        parseHTML(element) {
          return element.style.listStyleType || element.getAttribute('type');
        },
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const { type } = HTMLAttributes;
    const style = `list-style-type: ${type};`;
    return ['ul', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { style }), 0];
  },
  addCommands() {
    return {
      toggleBulletList: () => {
        return ({ chain, state }) => {
          const { from, to } = state.selection;
          const activeTextStyleAttrs = this.editor.getAttributes('textStyle');
          const isNewList = !this.editor.isActive('bulletList') && !this.editor.isActive('orderedList');
          return chain()
            .toggleList(this.name, this.options.itemTypeName)
            .updateAttributes(this.name, {
              ...this.editor.getAttributes('paragraph'),
            })
            .command(({ tr }) => {
              applyListToggleColors(tr, this.editor.schema, from, to, activeTextStyleAttrs, isNewList);
              return true;
            })
            .run();
        };
      },
      toggleBulletListType: (type?: string) => {
        return ({ chain, editor, commands }: CommandProps) => {
          // If no type is provided, toggle the list. Will apply default (disc) type.
          if (!type) {
            return chain().focus().toggleBulletList().run();
          }

          // If the list type is not set, set the list type.
          if (!editor.getAttributes(this.type).type) {
            return chain().focus().toggleBulletList().updateAttributes(this.type, { type }).run();
          }

          // If the list type is not the same as the type provided, update the list type.
          if (editor.getAttributes(this.type).type !== type) {
            return commands.updateAttributes(this.type, { type });
          }

          // If the list type is the same as the type provided, toggle the list.
          return chain().focus().toggleBulletList().run();
        };
      },
    };
  },
});
